// Trade-lane metadata: FreighTower's Chinese category → English SEO name + slug + blurb.
// lineCategoryId is the join key to the freight_rate_index views.
import { TRADES_I18N } from '../i18n/data.mjs'

export interface Trade {
  id: number
  slug: string
  name: string
  short: string
  blurb: string
}

export const TRADES: Trade[] = [
  {
    id: 18843,
    slug: 'asia-europe',
    name: 'Asia to Europe & Mediterranean',
    short: 'Asia–Europe',
    blurb:
      'Spot rates from China and Asia to North Europe (Rotterdam, Hamburg, Antwerp, Felixstowe) and the Mediterranean.',
  },
  {
    id: 18842,
    slug: 'asia-north-america',
    name: 'Asia to US & Canada',
    short: 'Asia–North America',
    blurb:
      'Trans-Pacific spot rates from Asia to US and Canadian ports (Los Angeles, Long Beach, New York, Vancouver).',
  },
  {
    id: 18844,
    slug: 'asia-latin-america',
    name: 'Asia to Latin America',
    short: 'Asia–Latin America',
    blurb: 'Spot rates from Asia to Latin America (Santos, Callao, Manzanillo, Buenaventura).',
  },
  {
    id: 18849,
    slug: 'intra-asia',
    name: 'Intra-Asia',
    short: 'Intra-Asia',
    blurb: 'Short-haul spot rates within Asia and the Indian subcontinent.',
  },
  {
    id: 18845,
    slug: 'asia-oceania',
    name: 'Asia to Australia & New Zealand',
    short: 'Asia–Oceania',
    blurb: 'Spot rates from Asia to Australia and New Zealand (Sydney, Melbourne, Auckland).',
  },
  {
    id: 18851,
    slug: 'asia-africa',
    name: 'Asia to Africa',
    short: 'Asia–Africa',
    blurb: 'Spot rates from Asia to African ports (Durban, Lagos, Mombasa, Dar es Salaam).',
  },
]

export const tradeById = (id: number | null | undefined) => TRADES.find((t) => t.id === id)
export const tradeBySlug = (slug: string) => TRADES.find((t) => t.slug === slug)

// POD country (ISO-2 prefix of the UN/LOCODE) → trade bucket id. Mirrors the SQL frm_trade_bucket()
// the index views use, so lane-level classification matches the index. IMPORTANT: the raw
// freight_rate_lane_latest.line_category_id is an unrelated FreighTower grouping (one id spans
// Europe+US+LatAm), so it must NOT be used to bucket lanes into trades — always classify by POD.
const POD_BUCKET: Record<string, number> = {
  US: 18842, CA: 18842,
  BR: 18844, AR: 18844, CL: 18844, PE: 18844, CO: 18844, MX: 18844, EC: 18844, PA: 18844,
  UY: 18844, PY: 18844, DO: 18844, GT: 18844, CR: 18844, VE: 18844, HN: 18844, SV: 18844,
  NI: 18844, BO: 18844,
  AU: 18845, NZ: 18845, FJ: 18845, PG: 18845,
  ZA: 18851, NG: 18851, KE: 18851, TZ: 18851, GH: 18851, CI: 18851, AO: 18851, SN: 18851,
  TG: 18851, CG: 18851, MZ: 18851, CM: 18851, GA: 18851, NA: 18851, MG: 18851, MU: 18851,
  SD: 18851, DJ: 18851, BJ: 18851,
  NL: 18843, DE: 18843, BE: 18843, GB: 18843, FR: 18843, ES: 18843, IT: 18843, GR: 18843,
  PL: 18843, TR: 18843, MA: 18843, EG: 18843, PT: 18843, DK: 18843, SE: 18843, IE: 18843,
  SI: 18843, HR: 18843, RO: 18843, BG: 18843, MT: 18843, CY: 18843, LT: 18843, LV: 18843,
  EE: 18843, FI: 18843, NO: 18843, IL: 18843, TN: 18843, DZ: 18843, LY: 18843, RU: 18843,
  UA: 18843,
}

/** Classify a lane into a trade bucket id by its destination country. Defaults to Intra-Asia. */
export function tradeBucket(podCode: string | null | undefined): number {
  return POD_BUCKET[(podCode ?? '').slice(0, 2).toUpperCase()] ?? 18849
}

// ── Localized trade labels (native, freight-desk voice) — from the shared i18n data ──

/** Localized { name, short } for a trade; falls back to the English source. */
export function tradeName(t: Trade, locale: string): { name: string; short: string } {
  const l = TRADES_I18N[locale]?.[t.id]
  return l ? { name: l.name, short: l.short } : { name: t.name, short: t.short }
}

/** Localized blurb for a trade; falls back to the English source. */
export function tradeBlurb(t: Trade, locale: string): string {
  return TRADES_I18N[locale]?.[t.id]?.blurb ?? t.blurb
}
